import { Location } from "gen/company/models_pb"
import { UUID } from "shared-ts/classes/uuid"
import { Chip } from "shared-web/components/base/informational/Chip"
import {
  Combobox,
  ComboboxProps,
} from "shared-web/components/base/input/Combobox"

import { useListLocationsForCompanyQuery } from "features/api/company"

interface LocationComboboxProps
  extends Omit<
    ComboboxProps<Location>,
    | "items"
    | "keyExtractor"
    | "valueExtractor"
    | "filterValueExtractor"
    | "eq"
    | "optionsAnchor"
  > {
  filterLocationfn?: (location: Location) => boolean
  filterActive?: boolean
  locations?: Location[]
}

export function LocationCombobox({
  filterLocationfn,
  filterActive = false,
  locations: locationsOverride,
  optionsClassName,
  ...comboboxProps
}: LocationComboboxProps) {
  const { data: listLocationsData } = useListLocationsForCompanyQuery(
    { filterActive },
    { skip: !!locationsOverride }
  )

  const locations = locationsOverride ?? listLocationsData?.locations ?? []
  const filteredLocations = filterLocationfn
    ? locations.filter(filterLocationfn)
    : locations

  const sortedLocations = [...filteredLocations].sort((a, b) =>
    a.name.localeCompare(b.name)
  )

  return (
    <Combobox<Location>
      eq={(a, b) => UUID.eqFromPB(a?.id, b?.id)}
      items={sortedLocations}
      keyExtractor={(location) => UUID.idString(location.id)}
      valueExtractor={(location) => location.name}
      filterValueExtractor={(location) => `${location.prefix} ${location.name}`}
      renderItemPrefix={(location) => (
        <Chip color="gray" text={location?.prefix ?? ""} />
      )}
      optionsAnchor={{
        to: "bottom start",
        gap: 8,
      }}
      optionsClassName={
        optionsClassName ?? "w-fit min-w-(--input-width) max-w-lg"
      }
      {...comboboxProps}
    />
  )
}
